import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import validator from "validator";
import isLandLine from "Globals/validators/LandLine";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import PersonViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PersonViewModel";

// Models
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";
import { PropertyDetailDTO } from "Globals/Models/DTO/PropertyDetailDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";

export default class PropertyDetailsViewModel extends FormSectionViewModelBase<PropertyDetailsModel> {
    @observable public personViewModels: PersonViewModel[] = [];

    constructor(propertyDetailsModel: PropertyDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, propertyDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.PropertyDetails, this.save);
        this.saveFunction = this.save;
        this.createViewModels();
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.PropertyDetails, this.canProceed);
        }
    }

    @action
    private createViewModels(): void {
        if (this.model.personModels) {
            for (const model of this.model.personModels) {
                this.personViewModels.push(new PersonViewModel(model));
            }
        }
    }

    @computed
    public get getSortedPersonViewModels(): PersonViewModel[] {
        return this.personViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertPropertyDetails: PropertyDetailDTO = this.model.toDto();
                const apiResult = await this.Post<PropertyDetailDTO>(AppUrls.Server.ApplicationForm.Upsert.PropertyDetails, upsertPropertyDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromPropertyDetailDto(apiResult.payload);
                    console.info("PropertyDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.PropertyDetails, this.canProceed);

                    let childForms = this.personViewModels.filter((a) => !a.model.propertyDetailId);
                    childForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("PropertyDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    /**
     * Clears conditional fields from the model.
     */
    private clearConditionalModelData(): void {
        if (this.model.isRentingProperty == false) {
            this.model.landlordPhone = "";
            this.model.landlordName = "";
            this.model.landlordAddressLine1 = "";
            this.model.landlordAddressLine2 = "";
            this.model.landlordAddressLine3 = "";
            this.model.landlordCity = "";
            this.model.landlordCounty = "";
            this.model.landlordPostcode = "";
        }
        if (this.model.isRegisteredWithHelpToBuy == false) {
            this.model.helpToBuyRegistrationNumber = "";
        }
        if (this.model.hasPets == false) {
            this.model.petsNotes = "";
        }
    }

    @action
    public handleInputChange = (): void => {
        this.clearConditionalModelData();
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.PropertyDetails, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<PropertyDetailsModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);

        if (fieldName != "isCouncilTenant") {
            this.model.setValue("isCouncilTenant", false);
        }
        if (fieldName != "isHousingAssociationTenant") {
            this.model.setValue("isHousingAssociationTenant", false);
        }
        if (fieldName != "isRentingProperty") {
            this.model.setValue("isRentingProperty", false);
        }
        if (fieldName != "isRentingFromEmployer") {
            this.model.setValue("isRentingFromEmployer", false);
        }
        if (fieldName != "isLivingWithFamily") {
            this.model.setValue("isLivingWithFamily", false);
        }
        if (fieldName != "isInTempAccommodation") {
            this.model.setValue("isInTempAccommodation", false);
        }
        if (fieldName != "isHomeOwner") {
            this.model.setValue("isHomeOwner", false);
        }

        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<PropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<PropertyDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<PropertyDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            //debugger;
            let field = _field as keyof FieldType<PropertyDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "personModels") return;
            if (field == "landlordPhone") return;
            if (field == "landlordAddressLine2") return;
            if (field == "landlordAddressLine3") return;
            if (field == "isRegisteredWithHelpToBuy") return;
            if (field == "helpToBuyRegistrationNumber") return;
            if (field == "createdDate") return;
            if (field == "isDeleted") return;

            if (this.model.isRentingProperty == true) {
                if (field == "landlordName") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "landlordAddressLine1") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "landlordCity") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "landlordCounty") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
                if (field == "landlordPostcode") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                }
            }
            // if (this.model.isRegisteredWithHelpToBuy == true) {
            //     if (field == "helpToBuyRegistrationNumber") {
            //         if (this.isEmptyNullOrUndefined(value)) {
            //             completed = false;
            //         }
            //         return;
            //     }
            // }
            if (this.model.hasPets == true) {
                if (field == "petsNotes") {
                    if (this.isEmptyNullOrUndefined(value)) {
                        completed = false;
                    }
                    return;
                }
            }
            if (this.model.isAnyoneLivingWithYou == true) {
                if (this.personViewModels.length < 1) {
                    completed = false;
                }
            }
            if (completed == false) {
                console.error("Property details field not completed: ", field, "Value: ", value);
            }
        });

        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<PropertyDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<PropertyDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        // if (fieldName == "isRegisteredWithHelpToBuy") {
        //     if (!value) {
        //         errorMessage = "Required to proceed";
        //         isValid = false;
        //     }
        // }

        if (fieldName == "landlordName") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.isRentingProperty && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "landlordPhone") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (value) {
                    if (this.model.isRentingProperty && !validator.isMobilePhone(value) && !isLandLine(value)) {
                        errorMessage = "telephone number is not valid";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "landlordAddressLine1") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.isRentingProperty && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "landlordCity") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.isRentingProperty && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "landlordCounty") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.isRentingProperty && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "landlordPostcode") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.isRentingProperty && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
                if (this.model.isRentingProperty && !validator.isPostalCode(value, "GB")) {
                    errorMessage = "not a valid postcode";
                    isValid = false;
                }
            }
        }
        // if (fieldName == "helpToBuyRegistrationNumber") {
        //     if ((value && !validator.isEmpty(value)) || isRequired) {
        //         if (this.model.isRegisteredWithHelpToBuy && validator.isEmpty(value)) {
        //             errorMessage = "required to proceed";
        //             isValid = false;
        //         }
        //     }
        // }
        if (fieldName == "petsNotes") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (this.model.hasPets && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "isAnyoneLivingWithYou") {
            if (value == true && this.personViewModels.length < 1) {
                errorMessage = "required to proceed";
                isValid = false;
            }
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<PropertyDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
